import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Download, BookOpen, ArrowLeft, ExternalLink, Search, X } from "lucide-react";
import { Link } from "wouter";
import { supabase } from "@/lib/supabase";
import LoadingSpinner from "@/components/LoadingSpinner";
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';

// Definición de tipos para los catálogos
interface Catalog {
  id: string;
  title: string;
  description: string;
  pdfUrl: string; // Este campo viene como pdf_url de la BD pero lo transformamos
  category: string;
  tags: string[];
  date: string;
}

export default function Catalogs() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedCatalog, setSelectedCatalog] = useState<Catalog | null>(null);
  const [showPDF, setShowPDF] = useState(false);
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [loading, setLoading] = useState(true);

  // Cargar catálogos desde Supabase al montar el componente
  useEffect(() => {
    async function loadCatalogs() {
      try {
        setLoading(true);

        // Obtener catálogos desde la tabla en Supabase
        const { data, error } = await supabase
          .from('catalogs')
          .select('*')
          .order('date', { ascending: false });

        if (error) {
          console.error("Error al cargar catálogos:", error);
          // Si falla la carga, establecer array vacío
          setCatalogs([]);
          return;
        }

        // Transformar los datos para que coincidan con la interfaz Catalog
        const formattedCatalogs: Catalog[] = data.map(item => ({
          id: item.id,
          title: item.title,
          description: item.description,
          pdfUrl: item.pdf_url,
          category: item.category,
          tags: item.tags || [],
          date: item.date
        }));

        setCatalogs(formattedCatalogs);
      } catch (error) {
        console.error("Error inesperado:", error);
        setCatalogs([]);
      } finally {
        setLoading(false);
      }
    }

    loadCatalogs();
  }, []);

  // Filtrar catálogos según búsqueda y categoría
  const filteredCatalogs = catalogs.filter(catalog => {
    const matchesSearch = searchQuery === "" ||
      catalog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      catalog.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      catalog.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCategory = selectedCategory === null || catalog.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Obtener categorías únicas
  const uniqueCategories = Array.from(new Set(catalogs.map(catalog => catalog.category)));

  // Función para obtener la URL pública
  const getPublicUrl = (pdfName: string) => {
    const { data } = supabase.storage
      .from('pdfs')
      .getPublicUrl(pdfName);

    return data?.publicUrl;
  };

  // Manejar la descarga del PDF
  const handleDownload = async (catalog: Catalog) => {
    if (!catalog.pdfUrl) {
      console.error('Error: URL del PDF no disponible');
      return;
    }
    const url = getPublicUrl(catalog.pdfUrl);
    if (url) {
      window.open(url, '_blank');
    }
  };

  // Manejar la visualización del PDF
  const handleViewPDF = async (catalog: Catalog) => {
    if (!catalog.pdfUrl) {
      console.error('Error: URL del PDF no disponible');
      return;
    }
    // Verificar si estamos en el cliente y el ancho de la pantalla
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    if (isMobile) {
      // En móvil: crear una URL amigable usando el título del catálogo
      // Crear un proxy o redireccionamiento a través de nuestra propia URL
      const fileName = catalog.pdfUrl.split('/').pop();
      // Usar un nombre más amigable basado en el título del catálogo
      const friendlyName = catalog.title
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]+/g, '');

      // Crear parámetros de consulta que contengan la información real del PDF
      const params = new URLSearchParams();
      params.append('file', catalog.pdfUrl);

      // Usar una ruta en nuestra propia aplicación
      const friendlyUrl = `/ver-catalogo/${friendlyName}?${params.toString()}`;

      window.open(friendlyUrl, '_blank');
    } else {
      // En escritorio: mostrar el modal
      setSelectedCatalog(catalog);
      setShowPDF(true);
    }
  };

  // Componente para visualizar PDF
  function PDFViewer({ url }: { url: string }) {
    const [pdfUrl, setPdfUrl] = useState<string>("");

    useEffect(() => {
      async function getPublicUrl() {
        try {
          const { data } = supabase.storage
            .from('pdfs')
            .getPublicUrl(url);

          if (data?.publicUrl) {
            // Añadir parámetros para optimizar la visualización del PDF
            setPdfUrl(`${data.publicUrl}#toolbar=0&navpanes=0&scrollbar=0`);
          }
        } catch (error) {
          console.error('Error al obtener la URL del PDF:', error);
        }
      }

      getPublicUrl();
    }, [url]);

    if (!pdfUrl) {
      return <div className="w-full h-[600px] flex items-center justify-center">
        <LoadingSpinner />
      </div>;
    }

    return (
      <iframe
        src={pdfUrl}
        className="w-full h-[80vh] rounded-lg border border-zinc-800"
        title="PDF Viewer"
        frameBorder="0"
        allowFullScreen
        style={{
          overflow: 'hidden'
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0B]">
      <SEO
        title="Biblioteca de Catálogos | Innotec Food Equipment"
        description="Explora y descarga nuestra colección completa de catálogos técnicos con información detallada sobre maquinaria industrial para el sector alimentario."
        canonicalUrl="/catalogos"
      />

      {/* Header */}
      <header className="relative py-6 border-b border-white/5 bg-gradient-to-b from-[#0A0A0B] to-[#121214]">
        <div className="container mx-auto px-4">
          <Link href="/">
            <div className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-3 cursor-pointer">
              <ArrowLeft className="w-4 h-4" />
              <span>Volver al inicio</span>
            </div>
          </Link>

          <PageHeader
            title="Biblioteca de catálogos"
            description="Explora nuestra colección completa de catálogos técnicos y descarga la información detallada"
          />

          {/* Buscador */}
          <div className="relative max-w-md mx-auto mt-8">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar catálogos..."
              className="w-full bg-zinc-800/50 text-white placeholder-zinc-500 rounded-lg pl-10 pr-4 py-2 outline-none border border-zinc-700/50 focus:border-[#FCAA0D]/50 transition-colors"
            />
          </div>
        </div>
      </header>

      {/* Filtros por categoría */}
      <div className="py-3 border-b border-white/5 bg-zinc-900/50">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
                ${selectedCategory === null
                  ? 'bg-[#FCAA0D] text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'
                }`}
            >
              Todos
            </button>
            {uniqueCategories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
                  ${selectedCategory === category
                    ? 'bg-[#FCAA0D] text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'
                  }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Modal para visualizar PDF */}
      <AnimatePresence>
        {showPDF && selectedCatalog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[999] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-zinc-900 rounded-xl w-full max-w-full sm:max-w-4xl lg:max-w-6xl p-2 sm:p-4 relative"
            >
              <button
                onClick={() => setShowPDF(false)}
                className="absolute top-4 right-4 text-zinc-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
              <h3 className="text-xl font-bold text-white mb-4">{selectedCatalog.title}</h3>
              <PDFViewer url={selectedCatalog.pdfUrl} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contenido principal */}
      <main className="py-10">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <LoadingSpinner />
            </div>
          ) : filteredCatalogs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
              {filteredCatalogs.map((catalog, index) => (
                <motion.div
                  key={catalog.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <div className="bg-zinc-900/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-zinc-800/50 group-hover:border-[#FCAA0D]/20 transition-all duration-300 h-full flex flex-col cursor-pointer">
                    <div className="h-48 bg-gradient-to-br from-[#FCAA0D]/20 to-[#FCAA0D]/5 relative overflow-hidden">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <FileText className="w-16 h-16 text-[#FCAA0D] opacity-50 group-hover:opacity-80 transition-opacity" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-zinc-900 to-transparent"></div>
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-zinc-800/80 text-white backdrop-blur-sm">
                          {catalog.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#FCAA0D] transition-colors">
                        {catalog.title}
                      </h3>
                      <p className="text-zinc-400 text-sm mb-6 flex-1">
                        {catalog.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {catalog.tags.map(tag => (
                          <span key={tag} className="px-2 py-1 rounded-md text-xs bg-zinc-800 text-zinc-400">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleViewPDF(catalog)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#FCAA0D]/10 text-[#FCAA0D] font-medium hover:bg-[#FCAA0D]/20 transition-colors"
                        >
                          <BookOpen className="w-4 h-4" />
                          <span>Ver online</span>
                        </button>
                        <button
                          onClick={() => handleDownload(catalog)}
                          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-zinc-800 text-white font-medium hover:bg-zinc-700 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="max-w-md mx-auto"
              >
                <FileText className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">No se encontraron catálogos</h3>
                <p className="text-zinc-400 mb-6">
                  No hay catálogos que coincidan con tu búsqueda. Intenta con otros términos o categorías.
                </p>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory(null);
                  }}
                  className="px-4 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700 transition-colors"
                >
                  Mostrar todos los catálogos
                </button>
              </motion.div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
